import type { TaxChoice } from './quote-pricing';

/**
 * Teklifte vergi seçimi (Ürün Sahibi, 09.10.2026): fiyat satırının yanında üç seçenek.
 * Sunucu modeli sürüm düzeyindedir (`QuoteVersion.taxMode`), bu yüzden her satırdaki üçlü seçim
 * aynı değeri okur ve yazar; bir satırda değiştirmek bütün teklifi değiştirir.
 */
export const VERGI_SECENEKLERI = ['INCLUSIVE', 'EXCLUSIVE', 'NONE'] as const satisfies readonly Exclude<TaxChoice, 'LEGACY'>[];

/** Yeni teklif: vergi üstüne eklenir, oran BAE KDV'si yüzde 5. */
export const VARSAYILAN_VERGI_KIPI: TaxChoice = 'EXCLUSIVE';
export const VARSAYILAN_VERGI_ORANI = '5';

/**
 * Kayıtlı sürümün kipi ekrana nasıl yüklenir. Boş (`null`) eski tekliftir ve eski davranışa
 * eşlenir: kalem başına elle girilen vergi, üstüne eklenir. Eski teklifin tutarı bu yüzden
 * yeniden açılınca da değişmez.
 */
export function kayitliVergiKipi(taxMode: string | null | undefined): TaxChoice {
  return taxMode === 'NONE' || taxMode === 'EXCLUSIVE' || taxMode === 'INCLUSIVE' ? taxMode : 'LEGACY';
}

/** Oran kutusu yalnız vergi uygulanan kiplerde anlamlı; `LEGACY` kalem başına oran girer. */
export function oranKutusuGorunur(kip: TaxChoice): boolean {
  return kip === 'EXCLUSIVE' || kip === 'INCLUSIVE';
}

/** Kalem anahtarları (`kalemMetni`): kalem kendi kipini/oranını bunlarda taşır; yoksa teklif varsayılanını miras alır. */
export const kalemKipAnahtari = (index: number) => `${index}:vergiKipi`;
export const kalemOranAnahtari = (index: number) => `${index}:vergiOrani`;

/** Satırdaki düğme: bu kalemin kipini değiştirir. Varsayılanla aynıysa kalem yeniden miras alır (açık kip tutulmaz). */
export function kalemKipiniAyarla(alanlar: Record<string, string>, index: number, kip: Exclude<TaxChoice, 'LEGACY'>, varsayilan: TaxChoice): Record<string, string> {
  const sonuc = { ...alanlar };
  if (kip === varsayilan) delete sonuc[kalemKipAnahtari(index)];
  else sonuc[kalemKipAnahtari(index)] = kip;
  return sonuc;
}

/** "Tüm satırlara uygula": varsayılan kip değişir, bütün kalemlerin açık kip ve oranı silinir. */
export function tumSatirlaraUygula(alanlar: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(alanlar).filter(([anahtar]) => !anahtar.endsWith(':vergiKipi') && !anahtar.endsWith(':vergiOrani')));
}

/** Herhangi bir satırın kendi kipi ya da oranı var mı (varsayılandan ayrılmış). */
export function satirlarFarkli(alanlar: Record<string, string>): boolean {
  return Object.keys(alanlar).some((anahtar) => anahtar.endsWith(':vergiKipi') || anahtar.endsWith(':vergiOrani'));
}

/** Kayıtlı kalemlerin açık kip/oranlarını düzenleme alanlarına yükler (oran yüzde yazısı: 500 -> "5,00"). */
export function kayitliKalemVergiAlanlari(items: { taxMode?: string | null; taxRateBps?: number | null }[], yuzdeYaz: (bps: number) => string): Record<string, string> {
  const alanlar: Record<string, string> = {};
  items.forEach((item, index) => {
    if (item.taxMode === 'NONE' || item.taxMode === 'EXCLUSIVE' || item.taxMode === 'INCLUSIVE') alanlar[kalemKipAnahtari(index)] = item.taxMode;
    if (item.taxRateBps !== null && item.taxRateBps !== undefined) alanlar[kalemOranAnahtari(index)] = yuzdeYaz(item.taxRateBps);
  });
  return alanlar;
}
